<script setup>
import { computed, provide, ref } from 'vue';
import { useI18n } from 'vue-i18n';
// import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import NewStaffChatModal from './NewStaffChatModal.vue';
import NewStaffGroupChatModal from './NewStaffGroupChatModal.vue';
import StaffChatContextMenuHost from './StaffChatContextMenuHost.vue';

const props = defineProps({
  activeChatId: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['selectChat', 'chatDeleted']);

const { t } = useI18n();
// const store = useStore();

const showNewChatModal = ref(false);
const showNewGroupChatModal = ref(false);
const staffChatListRef = ref(null);
const isContextMenuOpen = ref(false);

provide('contextMenuElementTarget', staffChatListRef);
provide('toggleContextMenu', state => {
  isContextMenuOpen.value = state;
});

const chats = useMapGetter('staffChats/getStaffChats');
const isLoading = useMapGetter('staffChats/getStaffChatListLoading');
const currentUser = useMapGetter('getCurrentUser');

const sortedChats = computed(() => {
  return [...chats.value].sort(
    (a, b) => (b.lastActivityAt || 0) - (a.lastActivityAt || 0)
  );
});

const getOtherUser = chat => chat.meta?.otherUser;

const isGroupChat = chat => chat.chatType === 'group';

const getChatTitle = chat => {
  if (chat.displayName) return chat.displayName;
  if (isGroupChat(chat)) return chat.name || t('STAFF_CHAT.NEW_GROUP_CHAT');
  return getOtherUser(chat)?.name;
};

const getLastMessagePreview = chat => {
  const messages = chat.messages || [];
  const last = messages[messages.length - 1];
  if (!last?.content) return '';

  const isOwnMessage = last.sender?.id === currentUser.value?.id;
  let senderPrefix = '';
  if (isOwnMessage) {
    senderPrefix = `${t('STAFF_CHAT.YOU')}: `;
  } else if (isGroupChat(chat)) {
    senderPrefix = `${last.sender?.name}: `;
  }

  return `${senderPrefix}${last.content}`;
};

const getTimestamp = chat => {
  const timestamp = chat.lastActivityAt;
  return timestamp ? shortTimestamp(dynamicTime(timestamp)) : '';
};

const onSelectChat = chat => {
  emit('selectChat', chat);
};

const onChatCreated = chat => {
  showNewChatModal.value = false;
  emit('selectChat', chat);
};

const onGroupChatCreated = chat => {
  showNewGroupChatModal.value = false;
  emit('selectChat', chat);
};

const onChatDeleted = chatId => {
  emit('chatDeleted', chatId);
};

const isActiveChat = chat => props.activeChatId === chat.id;
</script>

<template>
  <div
    class="flex flex-col w-full max-w-[340px] lg:w-[340px] h-full border-r border-n-weak bg-n-surface-1"
  >
    <div
      class="flex items-center justify-between gap-2 px-3 h-[3.25rem] border-b border-n-weak"
    >
      <h1 class="text-base font-medium text-n-slate-12">
        {{ $t('STAFF_CHAT.TITLE') }}
      </h1>
      <div class="flex items-center gap-1">
        <NextButton
          v-tooltip.top="$t('STAFF_CHAT.NEW_GROUP_CHAT')"
          icon="i-lucide-users"
          slate
          xs
          faded
          @click="showNewGroupChatModal = true"
        />
        <NextButton
          v-tooltip.top="$t('STAFF_CHAT.NEW_CHAT')"
          icon="i-lucide-plus"
          slate
          xs
          faded
          @click="showNewChatModal = true"
        />
      </div>
    </div>

    <div
      ref="staffChatListRef"
      class="flex-1 min-h-0 overflow-y-auto"
      :class="{ '!overflow-hidden': isContextMenuOpen }"
    >
      <div
        v-if="isLoading"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>
      <p
        v-else-if="!sortedChats.length"
        class="px-4 py-8 text-sm text-center text-n-slate-11"
      >
        {{ $t('STAFF_CHAT.EMPTY_LIST') }}
      </p>
      <StaffChatContextMenuHost
        v-for="chat in sortedChats"
        :key="chat.id"
        :chat="chat"
        @deleted="onChatDeleted(chat.id)"
      >
        <template #default="{ openContextMenu }">
          <button
            type="button"
            class="relative flex w-full gap-3 px-3 py-4 text-left border-b group/staff-chat-item border-n-slate-3 hover:border-n-surface-1 hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3 hover:z-[1]"
            :class="{
              'active animate-card-select bg-n-slate-6 !border-n-surface-1':
                isActiveChat(chat),
            }"
            @contextmenu="openContextMenu"
            @click="onSelectChat(chat)"
          >
            <Avatar
              v-if="!isGroupChat(chat)"
              :name="getOtherUser(chat)?.name"
              :src="getOtherUser(chat)?.thumbnail"
              :size="32"
              rounded-full
            />
            <div
              v-else
              class="flex items-center justify-center rounded-full size-8 bg-n-slate-3 text-n-slate-11"
            >
              <span class="text-base i-lucide-users" />
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center justify-between gap-2">
                <span class="text-sm font-medium truncate text-n-slate-12">
                  {{ getChatTitle(chat) }}
                </span>
                <span class="text-xs shrink-0 text-n-slate-10">
                  {{ getTimestamp(chat) }}
                </span>
              </div>
              <div class="flex items-center justify-between gap-2 mt-1">
                <p class="text-xs truncate text-n-slate-11">
                  {{ getLastMessagePreview(chat) }}
                </p>
                <span
                  v-if="chat.unreadCount > 0 && !isActiveChat(chat)"
                  class="flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-1 text-xs font-medium rounded-full bg-n-brand text-white"
                >
                  {{ chat.unreadCount }}
                </span>
              </div>
            </div>
          </button>
        </template>
      </StaffChatContextMenuHost>
    </div>

    <NewStaffChatModal
      v-if="showNewChatModal"
      @close="showNewChatModal = false"
      @created="onChatCreated"
    />
    <NewStaffGroupChatModal
      v-if="showNewGroupChatModal"
      @close="showNewGroupChatModal = false"
      @created="onGroupChatCreated"
    />
  </div>
</template>
